import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { amount, whole } from '@/lib/format';
import { Truncated, caption } from './parts';

type Customers = Extract<WireArtifact, { kind: 'customers' }>;
type Row = Customers['rows'][number];

/** One row per customer: how many loads, how they split by status, what they total. */
export function CustomersArtifact({ artifact }: { artifact: Customers }): JSX.Element {
  const most = Math.max(1, ...artifact.rows.map((r) => r.loads));
  return (
    <div className="flex flex-col gap-2">
      {artifact.rows.map((row, i) => (
        <CustomerRow key={i} row={row} most={most} />
      ))}
      {artifact.rows.length === 0 && (
        <p className="text-mute text-[13px]">No load named a customer.</p>
      )}
      {artifact.truncated && <Truncated what={`${artifact.rows.length} customers`} />}
    </div>
  );
}

function CustomerRow({ row, most }: { row: Row; most: number }): JSX.Element {
  return (
    <div className="border-line bg-surf rounded-xl border px-3.5 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-[13.5px] font-medium">{row.customer}</span>
        <span className="flex-none font-mono text-[13px] tabular-nums">{whole(row.loads)}</span>
      </div>
      <div className="bg-surf2 mt-2 h-1 overflow-hidden rounded-full">
        <div
          className="bg-acc h-full rounded-full"
          style={{ width: `${Math.max(4, (row.loads / most) * 100)}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {row.statuses.map((s, i) => (
          <span
            key={i}
            className="bg-surf2 text-mute rounded-full px-2 py-0.5 font-mono text-[10px]"
          >
            {s.status} {s.count}
          </span>
        ))}
      </div>
      {row.totalRate !== undefined && (
        <div className="mt-2 flex items-baseline justify-between gap-3">
          <span className={caption}>TOTAL RATE</span>
          <span className="font-mono text-[12.5px] tabular-nums">{amount(row.totalRate)}</span>
        </div>
      )}
    </div>
  );
}
